import { TreeView, TreeViewItem, Text } from '@payaca/ambrosia';
import {
  AnyCustomFieldDefinition,
  CUSTOM_FIELD_TYPE_READABLE_NAME_MAP,
  CustomFieldDefinition,
  FieldsetCustomFieldDefinition,
  SelectCustomFieldDefinition,
} from '@payaca/custom-fields';
import { FC, ReactNode, useMemo } from 'react';

/**
 * A custom field as returned by the GraphQL API, carrying the `JSONObject`
 * schema that fully describes the field (including any nested fieldset
 * children).
 */
export type CustomFieldDefinitionPreviewItem = {
  id: string;
  label: string;
  type: string;
  sortIndex: number;
  schema?: unknown;
};

/** Field details carried on each {@link TreeViewItem} via its `metaData`. */
type FieldMetaData = {
  typeName: string;
  /** Present only for select fields; undefined otherwise. */
  options?: string[];
};

const toTreeViewItem = (
  def: AnyCustomFieldDefinition,
  keyPrefix: string
): TreeViewItem => {
  const id = `${keyPrefix}.${def.identifier}`;

  return {
    id,
    label: def.label,
    metaData: {
      typeName: CUSTOM_FIELD_TYPE_READABLE_NAME_MAP[def.type] ?? def.type,
      options:
        def instanceof SelectCustomFieldDefinition
          ? def.options.filter((option): option is string => !!option)
          : undefined,
    } satisfies FieldMetaData,
    children:
      def instanceof FieldsetCustomFieldDefinition
        ? def.children.map((child) => toTreeViewItem(child, id))
        : undefined,
  };
};

const renderFieldLabel = (item: TreeViewItem): ReactNode => {
  const meta = item.metaData as FieldMetaData | undefined;
  // Fieldsets carry a (possibly empty) children array; leaf fields do not.
  const isFieldset = Array.isArray(item.children);

  if (isFieldset) {
    return (
      <div className="flex grow items-center gap-2 px-2">
        <h4>{item.label}</h4>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-end justify-between gap-2.5">
        <p className="break-words">{item.label}</p>
        {meta?.typeName && (
          <p className="supporting-body text-gray-500">{meta.typeName}</p>
        )}
      </div>

      {meta?.options && meta.options.length > 0 && (
        <p className="text-right text-gray-500 supporting-body mt-1">
          ({meta.options.join(', ')})
        </p>
      )}
    </div>
  );
};

export type CustomFieldDefinitionPreviewProps = {
  customFields: CustomFieldDefinitionPreviewItem[];
  emptyText?: string;
};

/**
 * Read-only preview of a custom fieldset's structure: every field's label and
 * type, with nested fieldsets shown as an indented tree. Renders the field
 * definitions only — never their values.
 */
const CustomFieldDefinitionPreview: FC<CustomFieldDefinitionPreviewProps> = (
  props
) => {
  const { customFields, emptyText = 'No fields in this fieldset.' } = props;

  const fieldTree = useMemo<TreeViewItem[]>(() => {
    return [...customFields]
      .sort((a, b) => a.sortIndex - b.sortIndex)
      .map((customField) => {
        try {
          const definition = CustomFieldDefinition.fromSchema(
            customField.schema as Parameters<
              typeof CustomFieldDefinition.fromSchema
            >[0]
          );
          return toTreeViewItem(definition, 'root');
        } catch {
          // Defensive: a malformed schema should not blank the whole preview.
          return {
            id: customField.id,
            label: customField.label,
            metaData: {
              typeName:
                CUSTOM_FIELD_TYPE_READABLE_NAME_MAP[
                  customField.type as keyof typeof CUSTOM_FIELD_TYPE_READABLE_NAME_MAP
                ] ?? customField.type,
            } satisfies FieldMetaData,
          };
        }
      });
  }, [customFields]);

  if (fieldTree.length === 0) {
    return (
      <div className="p-4">
        <Text colour="muted">{emptyText}</Text>
      </div>
    );
  }

  return <TreeView items={fieldTree} labelRenderer={renderFieldLabel} />;
};

export default CustomFieldDefinitionPreview;
